import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { throwIfFalsy } from '@stylehaven/components';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TokenRefreshStrategy } from './index';
import { UserTokens } from '../../../common/entities/user/user-tokens.entity';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { SharedApplicationState } from '../../../../state/shared-state.interface';
import { Logout } from '../../../../state/authentication/authentication.actions';
import { SetTokens } from '../../../../state/tokens/token.actions';

@Injectable()
export class RefreshRefreshTokenStrategy implements TokenRefreshStrategy {
    private readonly userTokens$: Observable<UserTokens>;
    constructor(
        private readonly router: Router,
        private readonly service: AuthenticationService,
        private readonly store: Store<SharedApplicationState>
    ) {
        this.userTokens$ = this.store.select<UserTokens>(state => state.token.userTokens);
    }

    refreshToken(
        req: HttpRequest<any>,
        next: HttpHandler,
        callBackSubject: Subject<string | undefined>
    ): Observable<HttpEvent<any>> {
        callBackSubject.next(undefined);
        return this.userTokens$.pipe(
            take(1),
            map((userTokens: UserTokens) => userTokens.refresh_token),
            throwIfFalsy('Empty refreshtoken'),
            switchMap((refreshToken: string) => this.service.refreshToken(refreshToken)),
            catchError(this.handleRefreshTokenError.bind(this)),
            tap(tokens => this.handleUserTokens(tokens, callBackSubject)),
            switchMap(() => next.handle(req))
        );
    }

    private handleRefreshTokenError(error: HttpErrorResponse): Observable<never> {
        this.store.dispatch(new Logout());

        // Make sure tokens are cleared before logging out
        return this.userTokens$.pipe(
            map(tokens => tokens.refresh_token),
            filter(refreshToken => !refreshToken),
            tap(() => {
                this.router.navigate(['/login']).then(() => {
                    // Should only trigger in offline mode since deploy redirects to oauth portal
                    // Used to clear memory
                    globalThis.location.reload();
                });
            }),
            switchMap(() => EMPTY) // Don't throw an error, since the 'isRefreshing' flag wouldn't be reset otherwise
        );
    }

    private handleUserTokens(userTokens: UserTokens, callbackSubject: Subject<string | undefined>): void {
        this.store.dispatch(new SetTokens(userTokens));
        callbackSubject.next(userTokens.refresh_token);
    }
}
